import React, { useState, useEffect, useCallback } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Card,
  CardContent,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Divider,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper
} from '@mui/material';
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  CompareArrows as CompareArrowsIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { compareStudents } from '../../services/teacherService';
import { ComparisonData } from '../../types/teacher';

interface StudentComparisonDialogProps {
  open: boolean;
  onClose: () => void;
  student1Id: number;
  student2Id: number;
  student1Name: string;
  student2Name: string;
}

const StudentComparisonDialog: React.FC<StudentComparisonDialogProps> = ({
  open,
  onClose,
  student1Id,
  student2Id,
  student1Name,
  student2Name
}) => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [comparisonData, setComparisonData] = useState<ComparisonData | null>(null);

  const fetchComparison = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await compareStudents(student1Id, student2Id);
      setComparisonData(data);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to load comparison';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  }, [student1Id, student2Id]);

  useEffect(() => {
    if (open) {
      fetchComparison();
    }
  }, [open, fetchComparison]);

  const getComparisonIcon = (value1: number, value2: number) => {
    if (value1 > value2) {
      return <TrendingUpIcon color="success" fontSize="small" />;
    } else if (value1 < value2) {
      return <TrendingDownIcon color="error" fontSize="small" />;
    }
    return null;
  };

  const getEfficiencyColor = (score: number): 'success' | 'warning' | 'error' => {
    if (score >= 80) return 'success';
    if (score >= 60) return 'warning';
    return 'error';
  };

  const renderMetricCard = (
    label: string,
    student1Value: number | string,
    student2Value: number | string,
    suffix = ''
  ) => {
    const numValue1 = typeof student1Value === 'number' ? student1Value : parseFloat(student1Value as string);
    const numValue2 = typeof student2Value === 'number' ? student2Value : parseFloat(student2Value as string);

    return (
      <Box mb={2}>
        <Typography variant="caption" color="text.secondary" gutterBottom display="block">
          {label}
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={6}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6" fontWeight="bold">
                {student1Value}{suffix}
              </Typography>
              {getComparisonIcon(numValue1, numValue2)}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {student1Name}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="h6" fontWeight="bold">
                {student2Value}{suffix}
              </Typography>
              {getComparisonIcon(numValue2, numValue1)}
            </Box>
            <Typography variant="caption" color="text.secondary">
              {student2Name}
            </Typography>
          </Grid>
        </Grid>
      </Box>
    );
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" gap={1}>
          <CompareArrowsIcon color="primary" />
          <Typography variant="h6">Student Comparison</Typography>
        </Box>
        <Typography variant="body2" color="text.secondary">
          {student1Name} vs {student2Name}
        </Typography>
      </DialogTitle>
      <DialogContent>
        {loading && (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight={300}>
            <CircularProgress />
          </Box>
        )}

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {!loading && !error && comparisonData && (
          <>
            {/* Side-by-side metrics */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Performance Metrics
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {renderMetricCard(
                  'Total Points',
                  comparisonData.students[0].totalPoints.toFixed(2),
                  comparisonData.students[1].totalPoints.toFixed(2)
                )}

                {renderMetricCard(
                  'Problems Solved',
                  comparisonData.students[0].problemsSolved,
                  comparisonData.students[1].problemsSolved
                )}

                {renderMetricCard(
                  'Average Attempts',
                  comparisonData.students[0].averageAttempts.toFixed(2),
                  comparisonData.students[1].averageAttempts.toFixed(2)
                )}

                {renderMetricCard(
                  'First-Try Success Rate',
                  comparisonData.students[0].firstTrySuccessRate.toFixed(1),
                  comparisonData.students[1].firstTrySuccessRate.toFixed(1),
                  '%'
                )}

                {renderMetricCard(
                  'Efficiency Score',
                  comparisonData.students[0].efficiencyScore.toFixed(1),
                  comparisonData.students[1].efficiencyScore.toFixed(1),
                  '%'
                )}

                {renderMetricCard(
                  'Accuracy',
                  comparisonData.students[0].accuracy.toFixed(1),
                  comparisonData.students[1].accuracy.toFixed(1),
                  '%'
                )}
              </CardContent>
            </Card>

            {/* Comparison table */}
            <Card variant="outlined" sx={{ mb: 3 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Detailed Comparison
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Metric</TableCell>
                        <TableCell align="right">{student1Name}</TableCell>
                        <TableCell align="right">{student2Name}</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      <TableRow>
                        <TableCell>Total Points</TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold">
                            {comparisonData.students[0].totalPoints.toFixed(2)}
                          </Typography>
                        </TableCell>
                        <TableCell align="right">
                          <Typography fontWeight="bold">
                            {comparisonData.students[1].totalPoints.toFixed(2)}
                          </Typography>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Problems Solved</TableCell>
                        <TableCell align="right">{comparisonData.students[0].problemsSolved}</TableCell>
                        <TableCell align="right">{comparisonData.students[1].problemsSolved}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Average Attempts</TableCell>
                        <TableCell align="right">
                          {comparisonData.students[0].averageAttempts.toFixed(2)}
                        </TableCell>
                        <TableCell align="right">
                          {comparisonData.students[1].averageAttempts.toFixed(2)}
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>First-Try Success</TableCell>
                        <TableCell align="right">
                          {comparisonData.students[0].firstTrySuccessRate.toFixed(1)}%
                        </TableCell>
                        <TableCell align="right">
                          {comparisonData.students[1].firstTrySuccessRate.toFixed(1)}%
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Efficiency Score</TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${comparisonData.students[0].efficiencyScore.toFixed(1)}%`}
                            color={getEfficiencyColor(comparisonData.students[0].efficiencyScore)}
                            size="small"
                          />
                        </TableCell>
                        <TableCell align="right">
                          <Chip
                            label={`${comparisonData.students[1].efficiencyScore.toFixed(1)}%`}
                            color={getEfficiencyColor(comparisonData.students[1].efficiencyScore)}
                            size="small"
                          />
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell>Accuracy</TableCell>
                        <TableCell align="right">
                          {comparisonData.students[0].accuracy.toFixed(1)}%
                        </TableCell>
                        <TableCell align="right">
                          {comparisonData.students[1].accuracy.toFixed(1)}%
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </CardContent>
            </Card>

            {/* Explanation */}
            <Alert severity="info" icon={<InfoIcon />}>
              <Typography variant="body2">
                <strong>Why Different Points?</strong> Points are awarded based on efficiency:
                1st attempt = 1.0 points, 2nd attempt = 0.5 points, 3rd attempt = 0.33 points, etc.
                A student with fewer average attempts earns more points per problem, even if they've
                solved the same number of problems. The efficiency score shows how close a student
                is to solving every problem on the first try.
              </Typography>
            </Alert>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="primary">
          Close
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StudentComparisonDialog;
